// src/mobile_pages/materials/MaterialBoard.jsx
import { useEffect, useMemo, useState, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import api from "../../api/axios";
import MobileSearchBar from "../../components/common/board/MobileSearchBar";
import MobileBoardPagination from "../../components/common/board/MobileBoardPagination";
import { fetchUserDetail } from "../../api/users/user";
import { formatDate } from "../../utils/date";

// ───────────────────────────────────────────────────────────────────────────────
// 라벨/매핑
const LABELS = {
  PS: "수급계획",
  PO: "발주서",
  GR: "입고",
  GI: "출고",
  QC: "검수",
  CK: "확인",
};
const ALL_TYPES = ["PS", "PO", "GR", "GI", "QC", "CK"];

const CONTEXT_BY_FORM = {
  PS: "plan",
  PO: "order",
  GR: "in",
  GI: "out",
  QC: "request",
  CK: "confirm",
};

// 품질 보드와 동일한 상태 배지 스타일
const statusColor = (status) => {
  switch (status) {
    case "submit":
    case "pending":
      return "bg-yellow-100 text-yellow-800";
    case "approved":
    case "done":
      return "bg-green-100 text-green-800";
    case "rejected":
    case "canceled":
      return "bg-red-100 text-red-800";
    default:
      return "bg-gray-100 text-gray-800";
  }
};
const statusText = (status) =>
({
  none: "초안",
  draft: "초안",
  submit: "상신",
  pending: "진행중",
  approved: "승인",
  done: "완료",
  rejected: "반려",
  cancel: "취소",
  canceled: "취소",
}[status] ||
  status ||
  "-");

// ───────────────────────────────────────────────────────────────────────────────
// 유틸
const DBG = (...args) => console.log("[MaterialBoard]", ...args);
const ERR = (...args) => console.error("[MaterialBoard]", ...args);

const parseMaybeJSON = (v) => {
  if (!v) return null;
  if (typeof v === "string") {
    try {
      return JSON.parse(v);
    } catch {
      return null;
    }
  }
  if (typeof v === "object") return v;
  return null;
};

// 회사명 제거, 한글 이름 합치기
const joinKoreanName = (last, first) => {
  const ln = (last || "").trim();
  const fn = (first || "").trim();
  if (ln && fn) return `${ln}${fn}`;
  return fn || ln || "";
};

const formatNameOnly = (uLike) => {
  if (!uLike) return "-";
  if (typeof uLike === "string") return uLike.trim() || "-";
  if (typeof uLike === "number") return `#${uLike}`;
  if (typeof uLike === "object") {
    const name =
      uLike.name ||
      joinKoreanName(uLike.last_name, uLike.first_name) ||
      uLike.username ||
      uLike.email ||
      "";
    return name.trim() || "-";
  }
  return "-";
};

/** 제목은 절대 dns로 떨어지지 않게 모든 경로 시도 */
const getTitle = (row) => {
  const direct =
    row?.title ||
    row?.name ||
    row?.subject ||
    row?.document_title ||
    row?.doc_title;
  if (typeof direct === "string" && direct.trim()) return direct.trim();

  const hdr =
    typeof row?.header === "string" ? parseMaybeJSON(row.header) : row?.header;
  const fromHeader =
    (hdr && (hdr.title || hdr?.values?.title || hdr?.meta?.title)) || null;
  if (typeof fromHeader === "string" && fromHeader.trim())
    return fromHeader.trim();

  const mmHdr =
    typeof row?.material_meta?.header === "string"
      ? parseMaybeJSON(row.material_meta.header)
      : row?.material_meta?.header;
  const fromMMHeader =
    (mmHdr && (mmHdr.title || mmHdr?.values?.title || mmHdr?.meta?.title)) ||
    null;
  if (typeof fromMMHeader === "string" && fromMMHeader.trim())
    return fromMMHeader.trim();

  const doc = row?.document || row?.content || {};
  const nested =
    doc?.title ||
    doc?.name ||
    doc?.subject ||
    (doc?.values && doc.values?.title) ||
    doc?.meta?.title;
  if (typeof nested === "string" && nested.trim()) return nested.trim();

  const extraObj =
    typeof row?.extra === "string" ? parseMaybeJSON(row.extra) : row?.extra;
  const body =
    parseMaybeJSON(extraObj?.content) ||
    extraObj?.content ||
    parseMaybeJSON(row?.template_snapshot) ||
    {};
  const fromExtra =
    body?.title ||
    body?.name ||
    body?.subject ||
    (body?.values && body.values?.title);
  if (typeof fromExtra === "string" && fromExtra.trim())
    return fromExtra.trim();

  if (typeof row?.ref_no === "string" && row.ref_no.trim())
    return row.ref_no.trim();

  return "제목 없음";
};

/** 서버가 무엇으로 주든 코드 대문자로 정규화 */
const getFormCode = (row) => {
  const raw =
    row?.form_type ?? row?.doc_type ?? row?.form ?? row?.type ?? row?.category;
  if (!raw) return null;
  const code = String(raw).trim().toUpperCase();

  if (ALL_TYPES.includes(code)) return code;

  const byLabel = Object.entries(LABELS).find(([, label]) => label === code);
  if (byLabel) return byLabel[0];

  const maybeCode = code.match(/\b(PS|PO|GR|GI|QC|CK)\b/);
  if (maybeCode) return maybeCode[1];

  return null;
};

// ───────────────────────────────────────────────────────────────────────────────

export default function MaterialBoard() {
  // 🔥 formType URL 파라미터도 함께 받기 (예: list/ps, list/all)
  const { id: projectId, formType } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const normalizedFormType = (formType || "all").toUpperCase();

  const loadCountRef = useRef(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);

  // 🔥 URL formType에 따른 초기 타입 설정
  const [types, setTypes] = useState(() =>
    normalizedFormType === "ALL"
      ? ALL_TYPES
      : ALL_TYPES.includes(normalizedFormType)
        ? [normalizedFormType]
        : ALL_TYPES
  );

  const [q, setQ] = useState("");
  const [ordering, setOrdering] = useState("-created_at"); // 기본 작성일 최신순

  // SearchFilterBar용 상태
  const [searchField, setSearchField] = useState("title"); // 제목/작성자/DNS
  const [dateFilter, setDateFilter] = useState("all"); // 작성일 전체/6m/3m/1m/custom
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [searchTrigger, setSearchTrigger] = useState(0);

  const [userCache, setUserCache] = useState({});
  const [deletingDns, setDeletingDns] = useState(null);

  const formTypeCsv = useMemo(() => types.join(","), [types]);

  // 🔥 URL formType 이 바뀌면 types를 다시 맞춰줌
  useEffect(() => {
    if (normalizedFormType === "ALL") {
      setTypes(ALL_TYPES);
    } else if (ALL_TYPES.includes(normalizedFormType)) {
      setTypes([normalizedFormType]);
    } else {
      setTypes(ALL_TYPES);
    }
    setPage(1);
  }, [normalizedFormType]);

  const applySearch = () => {
    setPage(1);
    setSearchTrigger((v) => v + 1); // 검색 버튼 눌렀을 때만 목록 새로고침
  };

  /** 작성자: 회사 제거, 이름만 */
  const renderWriter = (row) => {
    const directText =
      row?.writer_name ||
      row?.author_name ||
      row?.owner_name ||
      row?.created_by_name;
    if (typeof directText === "string" && directText.trim())
      return directText.trim();

    if (Array.isArray(row?.writer) && row.writer.length) {
      return formatNameOnly(row.writer[0] || {});
    }

    const cb = row?.created_by;
    if (cb && typeof cb === "object") {
      return formatNameOnly(cb);
    }
    if (typeof cb === "string" && cb.trim()) return cb.trim();

    const idNum =
      (typeof row?.created_by === "number" && row.created_by) ||
      (typeof row?.created_by_id === "number" && row.created_by_id) ||
      (typeof row?.created_by?.id === "number" && row.created_by.id) ||
      null;
    if (idNum && userCache[idNum]) {
      return formatNameOnly(userCache[idNum] || {});
    }

    return "-";
  };

  useEffect(() => {
    const load = async () => {
      const idx = ++loadCountRef.current;
      DBG(`LOAD#${idx} start`, {
        path: location.pathname,
        projectId,
        page,
        pageSize,
        ordering,
        formTypeCsv,
        q,
        searchField,
        startDate,
        endDate,
      });

      try {
        setLoading(true);
        setError("");

        const params = {
          project: projectId,
          page,
          page_size: pageSize,
          ordering,
          form_type: formTypeCsv,
          q: q.trim() || undefined,
          search_field: searchField,
          start_date: startDate || undefined,
          end_date: endDate || undefined,
        };

        const res = await api.get("material/docs/", { params });

        console.log(res);

        const items = res.data?.results || res.data;
        setRows(items);
        setCount(res.data?.count ?? (Array.isArray(items) ? items.length : 0));

        // 작성자 캐시 보강
        const ids = Array.from(
          new Set(
            (items || [])
              .map((r) => {
                if (typeof r?.created_by === "number") return r.created_by;
                if (typeof r?.created_by_id === "number")
                  return r.created_by_id;
                if (typeof r?.created_by?.id === "number")
                  return r.created_by.id;
                return null;
              })
              .filter(Boolean)
          )
        );

        const missing = ids.filter((id) => !userCache[id]);
        if (missing.length) {
          const pairs = await Promise.all(
            missing.map(async (id) => {
              try {
                const resU = await fetchUserDetail(id);
                const u = resU?.data || resU;
                return [id, u];
              } catch (e) {
                ERR(
                  `LOAD#${idx} fetch user ${id} failed`,
                  e?.response?.status,
                  e?.response?.data || e?.message
                );
                return [id, { error: 403 }];
              }
            })
          );
          setUserCache((prev) => ({ ...prev, ...Object.fromEntries(pairs) }));
        }
      } catch (e) {
        ERR(`LOAD#${idx} list error`, {
          message: e?.message,
          status: e?.response?.status,
          data: e?.response?.data,
          url: e?.config?.url,
          baseURL: e?.config?.baseURL,
          params: e?.config?.params,
          hasAuth: !!e?.config?.headers?.Authorization,
          authHead: e?.config?.headers?.Authorization || "(missing)",
        });
        setError("목록을 불러오지 못했습니다.");
      } finally {
        setLoading(false);
        DBG(`LOAD#${idx} end`);
      }
    };

    load();
  }, [
    projectId,
    page,
    pageSize,
    formTypeCsv,
    ordering,
    location.pathname,
    searchTrigger,
  ]); // eslint-disable-line

  const toggleType = (t) => {
    setPage(1);
    setTypes((prev) =>
      prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t].sort()
    );
  };

  const goView = (row) => {
    const code = getFormCode(row);
    const context = code ? CONTEXT_BY_FORM[code] : null;
    if (!context) return;
    const dns = row.doc_dns || row.dns;
    if (!dns) return;
    navigate(`/project/${projectId}/materials/${context}/${dns}/view`);
  };

  const handleDelete = async (row) => {
    const dns = row?.doc_dns || row?.dns;
    if (!dns) return;
    if (!window.confirm("이 문서를 삭제하시겠어요? 되돌릴 수 없습니다."))
      return;
    try {
      setDeletingDns(dns);
      await api.delete(`material/docs/${encodeURIComponent(dns)}/`, {
        params: { project: projectId },
      });
      setRows((prev) => prev.filter((r) => (r.doc_dns || r.dns) !== dns));
      setCount((c) => Math.max(0, c - 1));
    } catch (e) {
      const status = e?.response?.status;
      if (status === 404 || status === 405) {
        alert("서버가 문서 삭제 라우트를 제공하지 않습니다.");
      } else {
        ERR("delete error", {
          message: e?.message,
          status,
          data: e?.response?.data,
        });
        alert("삭제에 실패했습니다.");
      }
    } finally {
      setDeletingDns(null);
    }
  };

  // 현재 뷰의 타이틀 (전체/특정 타입)
  const currentTitle = (() => {
    if (normalizedFormType === "ALL") return "자재 허브(게시판)";
    if (ALL_TYPES.includes(normalizedFormType)) {
      return `${LABELS[normalizedFormType]} (${normalizedFormType}) 문서 목록`;
    }
    return "자재 허브(게시판)";
  })();

  // ───────────────────────────────────────────────────────────────────────────
  // 컬럼: 거래일 제거 → 작성일만 시:분 포함
  const columns = [
    {
      key: "title",
      header: "제목",
      align: "left", // <td> 정렬
      headerAlign: "center", // <th> 정렬
      render: (row) => (
        <div className="truncate max-w-[520px]">{getTitle(row)}</div>
      ),
    },
    {
      key: "form_type",
      header: "문서 유형",
      // width: 130,
      render: (row) => {
        const code = getFormCode(row);
        return (
          <span className="rounded-md border px-2 py-[2px] text-xs">
            {code ? `${LABELS[code]} (${code})` : "-"}
          </span>
        );
      },
    },
    {
      key: "status",
      header: "상태",
      // width: 110,
      render: (row) => (
        <span
          className={`px-2 py-0.5 text-xs font-medium rounded-full ${statusColor(
            row.status
          )}`}
        >
          {statusText(row.status)}
        </span>
      ),
    },
    {
      key: "writer",
      header: "작성자",
      // width: 160,
      render: renderWriter,
    },
    {
      key: "created_at",
      header: "작성일",
      // width: 180,
      render: (row) => formatDate(row.created_at),
    },
    {
      key: "actions",
      header: "관리",
      // width: 90,
      render: (row) => {
        const dns = row.doc_dns || row.dns;
        return (
          <button
            className="rounded-lg border px-2 py-1 text-xs hover:bg-gray-50 disabled:opacity-50"
            onClick={(e) => {
              e.stopPropagation();
              handleDelete(row);
            }}
            disabled={deletingDns === dns}
            title="문서 삭제"
          >
            {deletingDns === dns ? "삭제중…" : "삭제"}
          </button>
        );
      },
    },
  ];

  // 스켈레톤 로딩 컴포넌트
  const SkeletonCard = () => (
    <div className="bg-white rounded-lg p-4 animate-pulse border">
      <div className="flex justify-between items-start">
        <div className="flex-1 space-y-2">
          <div className="h-4 bg-gray-200 rounded w-3/4" />
          <div className="h-3 bg-gray-100 rounded w-1/2" />
        </div>
        <div className="h-8 w-8 bg-gray-100 rounded-full" />
      </div>
      <div className="mt-3 flex justify-between">
        <div className="h-3 bg-gray-100 rounded w-1/3" />
        <div className="h-3 bg-gray-100 rounded w-1/4" />
      </div>
    </div>
  );

  return (
    <div
      className="mobile-page-root mb-16"
      style={{ width: '100%', maxWidth: '100vw', minWidth: 0, display: 'flex', justifyContent: 'center', overflowX: 'hidden', boxSizing: 'border-box' }}
    >
      <div
        className="mobile-page-root-inner space-y-3"
        style={{ width: '100%', maxWidth: '480px', margin: '0 auto', padding: '0 1rem 1rem', boxSizing: 'border-box', overflowX: 'hidden' }}
      >
        {/* 헤더 */}
        <div className="bg-white rounded-lg p-4 border">
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0 flex-1">
              <h2 className="text-lg font-bold text-gray-900 truncate">
                {currentTitle}
              </h2>
              <p className="text-gray-500 text-xs mt-0.5">
                {normalizedFormType === "ALL"
                  ? "전체 자재 문서를 조회/검색합니다."
                  : "해당 유형의 자재 문서만 조회/검색합니다."}
              </p>
            </div>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-600">
            {error}
          </div>
        )}

        {/* 문서 유형 필터 */}
        <div className="bg-white rounded-lg border p-2 min-w-0 overflow-hidden">
          <div className="grid grid-cols-4 gap-1.5 min-w-0">
            <button
              onClick={() => {
                setTypes(ALL_TYPES);
                setPage(1);
              }}
              className={`px-1 py-2 rounded text-[11px] font-medium border transition-all text-center
                ${types.length === ALL_TYPES.length
                  ? "bg-blue-600 text-white border-blue-600"
                  : "bg-white text-gray-600 border-gray-200 hover:border-blue-300"
                }`}
            >
              전체
            </button>
            {ALL_TYPES.map((t) => {
              const active = types.length !== ALL_TYPES.length && types.includes(t);
              return (
                <button
                  key={t}
                  onClick={() => toggleType(t)}
                  className={`px-1 py-2 rounded text-[11px] font-medium border transition-all text-center
                    ${active
                      ? "bg-blue-600 text-white border-blue-600"
                      : "bg-white text-gray-600 border-gray-200 hover:border-blue-300"
                    }`}
                >
                  {LABELS[t]}
                  <span className="block text-[9px] opacity-70 mt-0.5 font-normal">{t}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* 검색 영역 */}
        <div className="bg-white rounded-lg border overflow-hidden min-w-0">
          <MobileSearchBar
            q={q}
            onChangeQ={setQ}
            onSearch={applySearch}
            searchField={searchField}
            onChangeSearchField={setSearchField}
            dateFilter={dateFilter}
            onChangeDateFilter={setDateFilter}
            startDate={startDate}
            endDate={endDate}
            onDateChange={({ start, end }) => {
              setStartDate(start);
              setEndDate(end);
            }}
            pageSize={pageSize}
            onChangePageSize={setPageSize}
          />
        </div>

        {/* 문서 리스트 */}
        <div className="space-y-2 min-w-0">
          {loading ? (
            <>
              <SkeletonCard />
              <SkeletonCard />
              <SkeletonCard />
            </>
          ) : rows.length === 0 ? (
            <div className="bg-white rounded-lg p-8 text-center border">
              <p className="text-sm text-gray-500">등록된 문서가 없습니다.</p>
            </div>
          ) : (
            rows.map((row) => {
              const code = getFormCode(row);
              const dns = row.doc_dns || row.dns;
              return (
                <button
                  key={dns}
                  onClick={() => goView(row)}
                  className="w-full text-left bg-white rounded-lg p-4 border hover:border-blue-300 active:bg-gray-50 transition-all"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-semibold text-gray-900 truncate">
                        {getTitle(row)}
                      </h3>
                      <div className="flex items-center gap-1.5 mt-1">
                        <span className="text-xs text-gray-500 truncate">
                          {renderWriter(row)}
                        </span>
                        <span className="text-xs text-gray-400">·</span>
                        <span className="text-xs text-gray-500">
                          {formatDate(row.created_at)}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="mt-3 flex flex-wrap gap-2">
                    {code && (
                      <span className="inline-flex items-center px-2 py-1 bg-gray-100 text-gray-600 rounded text-[11px]">
                        {LABELS[code]} ({code})
                      </span>
                    )}
                    <span
                      className={`px-2 py-1 text-xs font-medium rounded-full ${statusColor(
                        row.status
                      )}`}
                    >
                      {statusText(row.status)}
                    </span>
                  </div>
                </button>
              );
            })
          )}
        </div>

        {/* 페이지네이션 */}
        {!loading && rows.length > 0 && (
          <div className="bg-white rounded-lg p-3 border min-w-0">
            <MobileBoardPagination
              page={page}
              pageSize={pageSize}
              total={count}
              onPageChange={setPage}
            />
          </div>
        )}
      </div>
    </div>
  );
}
